/**
 * Universal AI Studio conversation shell.
 *
 * Chat is primary (including choice chips). Optional Outputs rail lists documents
 * (open in popup). Optional side panel (e.g. past runs) — not for decisions.
 *
 * Pill composer + center-when-empty follow planning chat patterns.
 * Composer is docked (fixed height); messages scroll in their own pane (keyboard-scrollable).
 */

import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ActiveContextIndicator } from './ActiveContextIndicator';
import { ConversationComposer, type ConversationComposerVariant } from './ConversationComposer';
import { ConversationMessageList } from './ConversationMessageList';
import type { ActiveConversationContext, ConversationMessage } from '@/types/aiStudio';

export interface ConversationModelOption {
  id: string;
  label: string;
  disabled?: boolean;
}

interface ConversationShellProps {
  context: ActiveConversationContext;
  messages: ConversationMessage[];
  sending: boolean;
  error?: string | null;
  onSend: (text: string) => void;
  disabled?: boolean;
  composerPlaceholder?: string;
  emptyStateText?: string;
  /** Past runs / secondary nav — not decision UI. */
  sidePanel?: React.ReactNode;
  /** Generic Outputs / Artifacts rail (documents open in popup). */
  outputsPanel?: React.ReactNode;
  hideContextIndicator?: boolean;
  models?: ConversationModelOption[];
  selectedModelId?: string;
  onModelChange?: (modelId: string) => void;
  className?: string;
  /** Card (default) or pill floating chatbox (roadmap / planning style). */
  composerVariant?: ConversationComposerVariant;
  /** Center pill composer vertically when thread is empty (planning embed pattern). */
  centerComposerWhenEmpty?: boolean;
  /** Rotating hint lines for empty pill composer. */
  composerHints?: readonly string[];
  composerHintStepMs?: number;
  /** Slot above composer dock (e.g. accept-suggestion CTA). */
  composerFooter?: React.ReactNode;
  /** @deprecated Composer is always docked; overlay layout removed. */
  composerFloating?: boolean;
  /** Parent pane already provides border/chrome (planning workspace split). */
  paneMode?: boolean;
  /** Undo from this user message (that turn and everything after). */
  onUndoTurn?: (userMessageId: string) => void;
  undoBusy?: boolean;
}

export const ConversationShell: React.FC<ConversationShellProps> = ({
  context,
  messages,
  sending,
  error,
  onSend,
  disabled = false,
  composerPlaceholder,
  emptyStateText,
  sidePanel,
  outputsPanel,
  hideContextIndicator = false,
  models,
  selectedModelId,
  onModelChange,
  className = '',
  composerVariant = 'card',
  centerComposerWhenEmpty = false,
  composerHints,
  composerHintStepMs = 2200,
  composerFooter,
  paneMode = false,
  onUndoTurn,
  undoBusy = false,
}) => {
  const { t } = useLanguage();
  const [composerFocused, setComposerFocused] = useState(false);
  const [hintIndex, setHintIndex] = useState(0);

  const showHints =
    composerVariant === 'pill' &&
    Boolean(composerHints?.length) &&
    messages.length === 0 &&
    !sending &&
    !composerFocused;

  useEffect(() => {
    if (!showHints || !composerHints?.length) return;
    setHintIndex(0);
    const id = window.setInterval(() => {
      setHintIndex((i) => (i + 1) % composerHints.length);
    }, composerHintStepMs);
    return () => clearInterval(id);
  }, [composerHintStepMs, composerHints, showHints]);

  const centerComposer =
    centerComposerWhenEmpty &&
    composerVariant === 'pill' &&
    messages.length === 0 &&
    !sending &&
    !composerFocused;

  const handleComposerFocus = () => {
    if (centerComposerWhenEmpty) {
      const sx = window.scrollX;
      const sy = window.scrollY;
      setComposerFocused(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (window.scrollX !== sx || window.scrollY !== sy) {
            window.scrollTo(sx, sy);
          }
        });
      });
      return;
    }
    setComposerFocused(true);
  };

  const messagePaneClass = paneMode
    ? 'relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-secondary-bg)]'
    : 'relative flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary-bg)]';

  return (
    <section
      className={`flex min-h-0 flex-1 flex-col ${paneMode ? 'h-full gap-0' : 'gap-2'} ${className}`}
      aria-label={t('aiStudio.conversation.title')}
    >
      {!hideContextIndicator && (
        <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2">
          <ActiveContextIndicator context={context} />
        </div>
      )}

      <div className={`flex min-h-0 flex-1 ${paneMode ? 'h-full' : 'gap-4'}`}>
        <div className={messagePaneClass}>
          {centerComposer && (
            <div className="min-h-0 w-full min-w-0 flex-1 shrink" aria-hidden />
          )}

          {!centerComposer && (
            <ConversationMessageList
              messages={messages}
              sending={sending}
              emptyStateText={emptyStateText}
              workspaceLayout={paneMode}
              onChoiceSelect={disabled || sending ? undefined : onSend}
              onUndoTurn={onUndoTurn}
              undoBusy={undoBusy}
            />
          )}

          {error && (
            <div
              role="alert"
              className="flex-shrink-0 border-t border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
            >
              {error}
            </div>
          )}

          {composerFooter && (
            <div className="flex-shrink-0 border-t border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2">
              {composerFooter}
            </div>
          )}

          <ConversationComposer
            onSend={onSend}
            disabled={disabled || sending}
            placeholder={composerPlaceholder}
            models={models}
            selectedModelId={selectedModelId}
            onModelChange={onModelChange}
            variant={composerVariant}
            hintText={showHints && composerHints ? composerHints[hintIndex] : null}
            showHintOverlay={showHints}
            onFocus={handleComposerFocus}
            onBlur={() => setComposerFocused(false)}
          />

          {centerComposer && (
            <div className="min-h-0 w-full min-w-0 flex-1 shrink" aria-hidden />
          )}
        </div>

        {(outputsPanel || sidePanel) && (
          <aside className="hidden w-80 shrink-0 flex-col gap-3 overflow-y-auto lg:flex">
            {outputsPanel}
            {sidePanel}
          </aside>
        )}
      </div>
    </section>
  );
};
